import { EMPTY_STRING } from "src/components/constant/constant";

export type CognitoConfig = {
  baseUrl: string;
  redirectUrl: string;
  oauthProvider: string;
  googleOAuthUrl: string;
};

export type AppConfig = {
  API_URL: string;
  COGNITO_USER_POOL_ID: string;
  COGNITO_CLIENT_ID: string;
  X_API_KEY: string;
  INTERNAL_API_KEY: string;
  PUBLIC_API_URL: string;
  FEEDBACK_URL: string;
  cognitoConfig: CognitoConfig;
  S3_ASSET_BUCKET: string;
  S3_FILE_BUCKET: string;
  REGION: string;
  IDENTITY_POOL_ID: string;
  PARTNER_ID: string;
  TEXT_EDITOR_KEY: string;
  SHOW_SSO_LOGIN: boolean;
  FIREBASE_CONFIG: {
    apiKey: string;
    authDomain: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
    measurementId: string;
  };
  XERO_CONFIG: {
    clientId: string;
    redirect_url: string;
    scopes: string;
  };
  MYOB_CONFIG: {
    clientId: string;
    redirect_url: string;
    scopes: string;
  };
  QUICKBOOKS_CONFIG: {
    clientId: string;
    redirect_url: string;
    scopes: string;
  };
  POSTHOG_API_KEY: string;
  POSTHOG_HOST: string;
};

let cachedHeaders: { clientId: string; redirectUrl: string; showSSOLogin: boolean } | null = null;

async function loadConfigFromEnvironment(): Promise<{ clientId: string; redirectUrl: string; showSSOLogin: boolean }> {
  if (cachedHeaders) return cachedHeaders;

  const hostname = window.location.hostname;
  const isLocal = hostname === "localhost" || hostname === "127.0.0.1";

  let clientId = EMPTY_STRING;
  let redirectUrl = EMPTY_STRING;
  let showSSOLogin = false;

  if (isLocal) {
    clientId = process.env.REACT_APP_COGNITO_CLIENT_ID || EMPTY_STRING;
    redirectUrl = process.env.REACT_APP_COGNITO_REDIRECT_URL || EMPTY_STRING;
    showSSOLogin = process.env.REACT_APP_SHOW_SSO_LOGIN === "true";
  } else {
    const hostPrefix = hostname.split(".gimmeit.net.au")[0].toUpperCase();

    const clientIdEnvVar = `REACT_APP_${hostPrefix}_CLIENT_ID`;
    const redirectUrlEnvVar = `REACT_APP_${hostPrefix}_REDIRECT_URL`;
    const showSsoLoginEnvVar = `REACT_APP_${hostPrefix}_SHOW_SSO_LOGIN`;

    // CPC runs on an Amplify domain (not *.gimmeit.net.au), so the tenant-prefixed
    // vars above won't exist — fall back to the static REACT_APP_COGNITO_* config.
    clientId =
      process.env[clientIdEnvVar] || process.env.REACT_APP_COGNITO_CLIENT_ID || EMPTY_STRING;
    redirectUrl =
      process.env[redirectUrlEnvVar] || process.env.REACT_APP_COGNITO_REDIRECT_URL || EMPTY_STRING;
    showSSOLogin =
      (process.env[showSsoLoginEnvVar] || process.env.REACT_APP_SHOW_SSO_LOGIN) === "true";
  }

  cachedHeaders = {
    clientId,
    redirectUrl,
    showSSOLogin
  };

  return cachedHeaders;
}

let config: AppConfig | null = null;

export async function loadConfig(): Promise<void> {
  const { clientId, redirectUrl, showSSOLogin } = await loadConfigFromEnvironment();

  config = {
    API_URL: process.env.REACT_APP_API_URL || EMPTY_STRING,
    COGNITO_USER_POOL_ID: process.env.REACT_APP_COGNITO_USER_POOL_ID || EMPTY_STRING,
    COGNITO_CLIENT_ID: clientId,
    X_API_KEY: process.env.REACT_APP_X_API_KEY || EMPTY_STRING,
    INTERNAL_API_KEY: process.env.REACT_APP_INTERNAL_API_KEY || EMPTY_STRING,
    PUBLIC_API_URL: process.env.REACT_APP_PUBLIC_API_URL || EMPTY_STRING,
    FEEDBACK_URL: process.env.REACT_APP_FEEDBACK_URL || EMPTY_STRING,
    S3_ASSET_BUCKET: process.env.REACT_APP_S3_ASSET_BUCKET || EMPTY_STRING,
    S3_FILE_BUCKET: process.env.REACT_APP_S3_FILE_BUCKET || EMPTY_STRING,
    REGION: process.env.REACT_APP_REGION || EMPTY_STRING,
    IDENTITY_POOL_ID: process.env.REACT_APP_IDENTITY_POOL_ID || EMPTY_STRING,
    PARTNER_ID: process.env.REACT_APP_PARTNER_ID || EMPTY_STRING,
    TEXT_EDITOR_KEY: process.env.REACT_APP_TEXT_EDITOR_KEY || EMPTY_STRING,
    SHOW_SSO_LOGIN: showSSOLogin,
    FIREBASE_CONFIG: {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY || EMPTY_STRING,
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || EMPTY_STRING,
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || EMPTY_STRING,
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || EMPTY_STRING,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || EMPTY_STRING,
      appId: process.env.REACT_APP_FIREBASE_APP_ID || EMPTY_STRING,
      measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || EMPTY_STRING
    },
    XERO_CONFIG: {
      clientId: process.env.REACT_APP_XERO_CLIENT_ID || EMPTY_STRING,
      redirect_url: process.env.REACT_APP_XERO_REDIRECT_URL || EMPTY_STRING,
      scopes: process.env.REACT_APP_XERO_SCOPES || EMPTY_STRING
    },
    MYOB_CONFIG: {
      clientId: process.env.REACT_APP_MYOB_CLIENT_ID || EMPTY_STRING,
      redirect_url: process.env.REACT_APP_MYOB_REDIRECT_URL || EMPTY_STRING,
      scopes: process.env.REACT_APP_MYOB_SCOPES || EMPTY_STRING
    },
    QUICKBOOKS_CONFIG: {
      clientId: process.env.REACT_APP_QUICKBOOKS_CLIENT_ID || EMPTY_STRING,
      redirect_url: process.env.REACT_APP_QUICKBOOKS_REDIRECT_URL || EMPTY_STRING,
      // Intuit accounting scope (+ openid profile for the connecting user's identity).
      scopes:
        process.env.REACT_APP_QUICKBOOKS_SCOPES || "com.intuit.quickbooks.accounting openid profile email"
    },
    POSTHOG_API_KEY: process.env.REACT_APP_POSTHOG_API_KEY || EMPTY_STRING,
    POSTHOG_HOST: process.env.REACT_APP_POSTHOG_HOST || EMPTY_STRING,
    cognitoConfig: {
      baseUrl: process.env.REACT_APP_COGNITO_BASE_URL || EMPTY_STRING,
      redirectUrl,
      oauthProvider:
        `${process.env.REACT_APP_COGNITO_BASE_URL}/oauth2/authorize?client_id=${clientId}&response_type=code&redirect_uri=${encodeURI(redirectUrl)}` ||
        EMPTY_STRING,
      googleOAuthUrl:
        `${process.env.REACT_APP_COGNITO_BASE_URL}/oauth2/authorize?identity_provider=Google&redirect_uri=${encodeURI(redirectUrl)}&response_type=code&client_id=${clientId}&scope=email+profile+openid` ||
        EMPTY_STRING
    }
  };
}

export function getConfig(): AppConfig {
  if (!config) throw new Error("Config not loaded yet");
  return config;
}

/**
 * The accounting providers this deployment can actually connect to.
 *
 * A provider is offered only where its OAuth client id is configured. That is
 * not a feature flag bolted on — without a client id the connect flow cannot
 * start, so offering the provider would hand the user a button that fails.
 *
 * It also decides, correctly and by itself, which providers each environment
 * exposes: sandbox sets MYOB and QuickBooks credentials on the Amplify branch,
 * production sets only Xero. Production therefore does not surface QuickBooks
 * while it is still under review, and turning it on later is one environment
 * variable rather than a code change — which is what previously forced the hub
 * to carry a prod-only copy commit and made every promotion conflict.
 *
 * Xero is listed first because it is the default provider.
 */
export type IntegrationProviderName = "xero" | "quickbooks" | "myob";

export function availableIntegrationProviders(): IntegrationProviderName[] {
  const { XERO_CONFIG, QUICKBOOKS_CONFIG, MYOB_CONFIG } = getConfig();
  const configured: [IntegrationProviderName, string][] = [
    ["xero", XERO_CONFIG?.clientId ?? EMPTY_STRING],
    ["quickbooks", QUICKBOOKS_CONFIG?.clientId ?? EMPTY_STRING],
    ["myob", MYOB_CONFIG?.clientId ?? EMPTY_STRING]
  ];

  return configured.filter(([, clientId]) => clientId.trim().length > 0).map(([name]) => name);
}

export function isIntegrationProviderAvailable(provider: string): boolean {
  return availableIntegrationProviders().includes(provider as IntegrationProviderName);
}
